import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useYieldCallback } from '@zillow/yield-callback';
import { styled } from '@/styled-system/jsx';
import type { RootReduxState } from 'app/shared/flux/reducers';
import type { ListingDetails } from 'app/types/listingDetails.type';

// Components
import Button from 'app/shared/core/Button';
import PoweredByZillowBadge from 'app/shared/modules/PoweredByZillowBadge';

// Misc / Utils
import IconApplicationsTeal from 'images/icons/applications-teal.svg';
import { shouldShowApplyButton } from 'app/shared/utils/cometUtils';
import { emitTrackApplyNowClickEvent } from 'app/shared/models/Clickstream/HdpClickstreamEvents';
import { emitTrackRequestContactStartEvent } from 'app/shared/models/Clickstream/ContactClickstreamEvents';

// Experiments
import constants from 'app/shared/constants/ExperimentConstants';

interface ZillowApplicationsButtonProps {
  showContactButton?: boolean;
  onActionPopup?: (popupName: string) => void;
}

const PoweredByZillowContainer = styled('div', {
  base: {
    marginTop: '1x',
    textAlign: 'center',
  },
});

const VERBIAGE_CONSTANTS = {
  true: {
    button: 'Apply now',
    footer: 'Powered by',
  },
  false: {
    button: 'Request to apply',
    footer: 'Powered by',
  },
};

const ZillowApplicationsButton: React.FC<ZillowApplicationsButtonProps> = ({ onActionPopup }) => {
  // Redux
  useDispatch(); // Available if needed
  const listing = useSelector(
    (state: RootReduxState) => state.currentListingDetails.currentListing,
  ) as ListingDetails | null;

  // Other vars needed for rendering components
  const rentalApplicationStatus = listing?.rentalApplicationStatus || '';
  const acceptsApplications = shouldShowApplyButton(rentalApplicationStatus);

  // A/B Test: CTA Copy Test
  const ctaExperiment = useSelector(
    (state: RootReduxState) => state.analytics.runningExperiments[constants.HOTPADS_WEB_CTA_COPY_TEST],
  );

  const yieldTrackAndOpenModal = useYieldCallback(() => {
    onActionPopup?.('zillowApplications');
    if (acceptsApplications) {
      // Fire tracking for initial 'Apply now' button click
      emitTrackApplyNowClickEvent();
    } else {
      // Fire request applications tracking (e.g., "Request to apply" button)
      emitTrackRequestContactStartEvent({
        listing,
      });
    }
  });

  const handleButtonClick = useCallback(() => {
    yieldTrackAndOpenModal();
  }, [yieldTrackAndOpenModal]);

  // Determine button text with experiment overrides
  let buttonText = VERBIAGE_CONSTANTS[acceptsApplications ? 'true' : 'false'].button;

  // Apply CTA experiment overrides for standard properties only (not application-enabled)
  if (ctaExperiment && !ctaExperiment.isControl && !acceptsApplications) {
    if (ctaExperiment.treatment === 'variant1' || ctaExperiment.treatment === 'variant2') {
      buttonText = 'Request a tour';
    }
    // If experiment exists but treatment doesn't match, fall back to control behavior
  }

  return (
    <div>
      <Button
        btnType={'primary-outline'}
        onClick={handleButtonClick}
        rounded={false}
        full
        bold
        size={'lg'}
        icon={{ src: IconApplicationsTeal, width: '22px', height: '20px', alt: '' }}
        data-testid="zillow-applications-button"
      >
        {buttonText}
      </Button>
      {acceptsApplications && (
        <PoweredByZillowContainer>
          <PoweredByZillowBadge />
        </PoweredByZillowContainer>
      )}
    </div>
  );
};

export default ZillowApplicationsButton;
